import { create } from 'zustand';

export type Theme = 'dark' | 'light';

const THEME_STORAGE_KEY = 'dda-theme';
const RAIL_STORAGE_KEY = 'dda-rail';

function readInitialTheme(): Theme {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === 'dark' || stored === 'light') return stored;
    // Light is the product default; a saved choice above still wins.
    return 'light';
  } catch {
    return 'light';
  }
}

/** The rail starts open — it IS the navigation now, and a shell that boots
 *  collapsed hides the history the screen exists to show. Only a deliberate
 *  collapse persists. */
function readInitialRailCollapsed(): boolean {
  try {
    return localStorage.getItem(RAIL_STORAGE_KEY) === 'collapsed';
  } catch {
    return false;
  }
}

function persistRail(collapsed: boolean): void {
  try {
    localStorage.setItem(RAIL_STORAGE_KEY, collapsed ? 'collapsed' : 'open');
  } catch {
    /* ignore */
  }
}

/** Reflect the theme on <html data-theme> and persist the raw value. */
function applyTheme(theme: Theme): void {
  document.documentElement.setAttribute('data-theme', theme);
  try {
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    /* ignore */
  }
}

interface UiState {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
  /** Desktop rail state. The mobile drawer is local to the shell — a drawer left
   *  open across navigations would cover the screen it navigated to. */
  railCollapsed: boolean;
  toggleRail: () => void;
}

export const useUiStore = create<UiState>((set, get) => ({
  theme: readInitialTheme(),
  setTheme: (theme) => {
    applyTheme(theme);
    set({ theme });
  },
  toggleTheme: () => {
    const next: Theme = get().theme === 'dark' ? 'light' : 'dark';
    applyTheme(next);
    set({ theme: next });
  },
  railCollapsed: readInitialRailCollapsed(),
  toggleRail: () => {
    const next = !get().railCollapsed;
    persistRail(next);
    set({ railCollapsed: next });
  },
}));

// Keep the DOM in sync with the initial store value (matches the pre-paint boot script).
applyTheme(useUiStore.getState().theme);
